import { Flex, Form, Skeleton, Spin } from "antd"
import React, { useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { ROLE_IDS } from "../../../constants/roles.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { type RootState } from "../../../store.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { type RequestAccountDeletionParams } from "../api/types.ts"
import { SettingsProfileForm } from "../components/settings-profile/SettingsProfileForm.tsx"
import {
  changeEmail,
  fetchUser,
  requestAccountDeletion,
  updatePassword,
  updateUser
} from "../redux/settings.thunks.ts"
import { type SettingProfileFormData } from "../types/types.ts"

const SettingsProfilePage = ({
  userId,
  fullUser,
  fetching,
  saveFetching,
  deleteAccountFetching,
  updatePassword,
  changeEmailRequest,
  //
  getUser,
  updateUser,
  requestAccountDeletion,
  logout
}: ConnectedProps<typeof connector>) => {
  const navigate = useNavigate()
  const [form] = Form.useForm<SettingProfileFormData>()

  const onReset = () => form.resetFields()

  const handleFinish = async ({
    confirm_new_password,
    new_password,
    current_password,
    email,
    avatar,
    ...data
  }: SettingProfileFormData) => {
    try {
      await updateUser({
        id: userId,
        ...(typeof avatar !== "string" ? { avatar } : {}),
        ...data
      }).unwrap()

      form.resetFields([
        "first_name",
        "middle_name",
        "last_name",
        "avatar",
        "phone"
      ])

      if (email && email !== fullUser?.email) {
        await changeEmailRequest({ new_email: email }).unwrap()
        form.resetFields(["email"])
      }

      if (current_password && new_password && confirm_new_password) {
        await updatePassword({
          id: userId,
          current_password,
          new_password,
          confirm_new_password
        }).unwrap()

        form.resetFields([
          "current_password",
          "new_password",
          "confirm_new_password"
        ])
        await logout({ navigate })
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    generateDocumentTitle("Settings")
    if (userId) getUser(userId)
  }, [userId, getUser])

  const handleRequestAccountDeletion = async (
    params: RequestAccountDeletionParams
  ) => {
    await requestAccountDeletion(params).unwrap()
    try {
      await logout({ navigate }).unwrap()
    } catch {
      window.location.assign(RoutesPaths.auth.login)
    }
  }

  return (
    <>
      {fetching ? (
        <Flex flex={1}>
          <Spin fullscreen size="large" />
          <Skeleton.Node className={"size-full"} />
        </Flex>
      ) : (
        <>
          {fullUser ? (
            <SettingsProfileForm
              form={form}
              saving={saveFetching}
              onReset={onReset}
              onFinish={handleFinish}
              roleId={fullUser?.role?.id}
              userId={userId ?? ""}
              canDeleteAccount={
                fullUser?.role?.id !== ROLE_IDS.ADMIN_PLATFORM &&
                fullUser?.role?.id !== ROLE_IDS.ADMIN_GCC
              }
              accountDeletionRequestedAt={
                fullUser?.account_deletion_requested_at
              }
              deleteAccountFetching={deleteAccountFetching}
              onRequestAccountDeletion={handleRequestAccountDeletion}
              initialValues={{
                ...fullUser,
                confirm_new_password: undefined,
                new_password: undefined,
                current_password: undefined,
                role: fullUser?.role?.name
              }}
              GCCOwners={fullUser?.gcc_owners}
            />
          ) : (
            <div>User not found</div>
          )}
        </>
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.settings.fetching,
  saveFetching:
    state.settings.saveFetching || state.settings.savePasswordFetching,
  deleteAccountFetching: state.settings.deleteAccountFetching,

  fullUser: state.settings.user,
  userId: state?.auth?.userInfo?.id
})

const mapDispatchToProps = {
  getUser: fetchUser,
  updateUser,
  updatePassword: updatePassword,
  changeEmailRequest: changeEmail,
  requestAccountDeletion,
  logout
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SettingsProfilePage)
